/**
 * Orbital mechanics calculations for computing positions from orbital elements
 */
import * as Astronomy from 'astronomy-engine';

/**
 * Convert Julian Date to JavaScript Date
 */
export function jdToDate(jd) {
  const millis = (jd - 2440587.5) * 86400000;
  return new Date(millis);
}

/**
 * Convert JavaScript Date to Julian Date
 */
export function dateToJD(date) {
  return date.getTime() / 86400000 + 2440587.5;
}

/**
 * Solve Kepler's equation using Newton-Raphson method
 * M = E - e * sin(E)
 */
function solveKeplerEquation(M, e, tolerance = 1e-8) {
  // Initial guess
  let E = M;
  if (e > 0.8) {
    E = Math.PI;
  }
  
  // Newton-Raphson iteration
  for (let i = 0; i < 50; i++) {
    const delta = E - e * Math.sin(E) - M;
    const deltaE = delta / (1 - e * Math.cos(E));
    E -= deltaE;
    
    if (Math.abs(deltaE) < tolerance) {
      break;
    }
  }
  
  return E;
}

/**
 * Compute current position of a solar system object from orbital elements
 * @param {Object} elements - Orbital elements
 * @param {number} elements.epoch - Epoch of elements (JD)
 * @param {number} elements.a - Semi-major axis (AU)
 * @param {number} elements.e - Eccentricity
 * @param {number} elements.i - Inclination (degrees)
 * @param {number} elements.omega - Argument of perihelion (degrees)
 * @param {number} elements.node - Longitude of ascending node (degrees)
 * @param {number} elements.M - Mean anomaly at epoch (degrees)
 * @param {number} elements.n - Mean motion (degrees/day)
 * @param {number} elements.tp - Time of perihelion (JD) for comets
 * @param {number} elements.q - Perihelion distance (AU)
 * @param {Date} targetDate - Date to compute position for (defaults to now)
 * @returns {Object} {ra, dec} in degrees
 */
export function computePositionFromElements(elements, targetDate = new Date()) {
  const DEG_TO_RAD = Math.PI / 180;
  const RAD_TO_DEG = 180 / Math.PI;
  
  // Validate required elements
  if (!elements || typeof elements.e !== 'number' || typeof elements.i !== 'number') {
    console.error('Invalid orbital elements provided:', elements);
    return { ra: 0, dec: 0 };
  }
  
  // Convert elements to radians
  const i = elements.i * DEG_TO_RAD;
  const omega = elements.omega * DEG_TO_RAD;
  const node = elements.node * DEG_TO_RAD;
  
  // Calculate time since epoch or perihelion
  const currentJD = dateToJD(targetDate);
  
  let r, v; // radius and true anomaly
  
  // Check if this is a comet with extreme eccentricity
  if (elements.e >= 0.98 && elements.tp) {
    // Use time since perihelion for near-parabolic and hyperbolic orbits
    const t = currentJD - elements.tp;
    const q = elements.q || elements.a * (1 - elements.e);
    
    if (Math.abs(elements.e - 1.0) < 0.01) {
      // Near-parabolic orbit (0.99 < e < 1.01)
      // Use Barker's equation for parabolic approximation
      const W = 0.01720209895 * t / Math.pow(q, 1.5);
      const G = W / 2;
      let Y;
      
      // Solve Y^3 + 3Y - 2G = 0 using Newton-Raphson
      Y = G; // Initial guess
      for (let i = 0; i < 10; i++) {
        const Y3 = Y * Y * Y;
        const f = Y3 + 3 * Y - 2 * G;
        const fp = 3 * Y * Y + 3;
        Y = Y - f / fp;
      }
      
      const tanHalfV = Y;
      v = 2 * Math.atan(tanHalfV);
      r = q * (1 + tanHalfV * tanHalfV);
    } else if (elements.e > 1) {
      // Hyperbolic orbit
      const a = q / (elements.e - 1); // Note: a is negative for hyperbolic orbits
      const n = 0.01720209895 / Math.pow(Math.abs(a), 1.5);
      const M = n * t;
      
      // Solve hyperbolic Kepler equation: M = e*sinh(H) - H
      let H = M; // Initial guess
      for (let i = 0; i < 10; i++) {
        const sinhH = Math.sinh(H);
        const coshH = Math.cosh(H);
        const f = elements.e * sinhH - H - M;
        const fp = elements.e * coshH - 1;
        H = H - f / fp;
      }
      
      // Calculate true anomaly
      const tanhHalf = Math.tanh(H / 2);
      const tanHalfV = Math.sqrt((elements.e + 1) / (elements.e - 1)) * tanhHalf;
      v = 2 * Math.atan(tanHalfV);
      
      // Calculate radius
      r = Math.abs(a) * (elements.e * Math.cosh(H) - 1);
    } else {
      // Elliptical orbit (e < 0.99)
      const a = q / (1 - elements.e);
      const n = 0.01720209895 / Math.pow(a, 1.5);
      const M = n * t;
      const E = solveKeplerEquation(M, elements.e);
      const sinE = Math.sin(E);
      const cosE = Math.cos(E);
      const sqrtOneMinusE2 = Math.sqrt(1 - elements.e * elements.e);
      const sinV = (sqrtOneMinusE2 * sinE) / (1 - elements.e * cosE);
      const cosV = (cosE - elements.e) / (1 - elements.e * cosE);
      v = Math.atan2(sinV, cosV);
      r = a * (1 - elements.e * cosE);
    }
  } else {
    // Standard elliptical orbit calculation
    const M0 = elements.M * DEG_TO_RAD;
    const daysSinceEpoch = currentJD - elements.epoch;
    
    // Calculate mean motion if not provided
    const n = elements.n ? elements.n * DEG_TO_RAD : 
              2 * Math.PI / (elements.P * 365.25); // radians/day
    
    // Current mean anomaly
    const M = M0 + n * daysSinceEpoch;
    
    // Solve Kepler's equation for eccentric anomaly
    const E = solveKeplerEquation(M, elements.e);
    
    // Calculate true anomaly
    const sinE = Math.sin(E);
    const cosE = Math.cos(E);
    const sqrtOneMinusE2 = Math.sqrt(1 - elements.e * elements.e);
    
    const sinV = (sqrtOneMinusE2 * sinE) / (1 - elements.e * cosE);
    const cosV = (cosE - elements.e) / (1 - elements.e * cosE);
    v = Math.atan2(sinV, cosV);
    
    // Calculate radius
    r = elements.a * (1 - elements.e * cosE);
  }
  
  // Position in orbital plane
  const x_orbital = r * Math.cos(v);
  const y_orbital = r * Math.sin(v);
  
  // Convert to heliocentric ecliptic coordinates
  const cosOmega = Math.cos(omega);
  const sinOmega = Math.sin(omega);
  const cosNode = Math.cos(node);
  const sinNode = Math.sin(node);
  const cosI = Math.cos(i);
  const sinI = Math.sin(i);
  
  const x_ecliptic = (cosOmega * cosNode - sinOmega * sinNode * cosI) * x_orbital +
                     (-sinOmega * cosNode - cosOmega * sinNode * cosI) * y_orbital;
  
  const y_ecliptic = (cosOmega * sinNode + sinOmega * cosNode * cosI) * x_orbital +
                     (-sinOmega * sinNode + cosOmega * cosNode * cosI) * y_orbital;
  
  const z_ecliptic = (sinOmega * sinI) * x_orbital +
                     (cosOmega * sinI) * y_orbital;

  // Convert heliocentric to geocentric coordinates
  // We need to subtract Earth's heliocentric position to get the geocentric position
  try {
    const earthVector = Astronomy.HelioVector(Astronomy.Body.Earth, Astronomy.MakeTime(targetDate));

    // Convert Earth's ICRF equatorial position to ecliptic
    const epsilon = 23.43929111 * DEG_TO_RAD; // Obliquity of ecliptic for J2000
    const cosEpsilon = Math.cos(epsilon);
    const sinEpsilon = Math.sin(epsilon);

    const earth_x_ecliptic = earthVector.x;
    const earth_y_ecliptic = cosEpsilon * earthVector.y + sinEpsilon * earthVector.z;
    const earth_z_ecliptic = -sinEpsilon * earthVector.y + cosEpsilon * earthVector.z;

    // Geocentric ecliptic position = heliocentric asteroid - heliocentric Earth
    const x_ecliptic_geo = x_ecliptic - earth_x_ecliptic;
    const y_ecliptic_geo = y_ecliptic - earth_y_ecliptic;
    const z_ecliptic_geo = z_ecliptic - earth_z_ecliptic;

    // Convert geocentric ecliptic to equatorial coordinates (J2000)
    const x_equatorial = x_ecliptic_geo;
    const y_equatorial = cosEpsilon * y_ecliptic_geo - sinEpsilon * z_ecliptic_geo;
    const z_equatorial = sinEpsilon * y_ecliptic_geo + cosEpsilon * z_ecliptic_geo;

    // Convert to RA/Dec
    const ra = Math.atan2(y_equatorial, x_equatorial) * RAD_TO_DEG;
    const dec = Math.atan2(z_equatorial,
                           Math.sqrt(x_equatorial * x_equatorial + y_equatorial * y_equatorial)) * RAD_TO_DEG;

    // Normalize RA to 0-360
    const raNormalized = ra < 0 ? ra + 360 : ra;

    // Validate results
    if (isNaN(raNormalized) || isNaN(dec)) {
      console.error('Orbital calculation resulted in NaN:', {
        r, v, x_orbital, y_orbital,
        x_ecliptic, y_ecliptic, z_ecliptic,
        x_equatorial, y_equatorial, z_equatorial,
        ra, dec, elements
      });
      return { ra: 0, dec: 0 };
    }

    return {
      ra: raNormalized,
      dec: dec
    };
  } catch (error) {
    console.error('Error converting heliocentric to geocentric coordinates:', error);
    // Fallback to approximate heliocentric position if astronomy-engine fails
    const epsilon = 23.43929111 * DEG_TO_RAD;
    const cosEpsilon = Math.cos(epsilon);
    const sinEpsilon = Math.sin(epsilon);

    const x_equatorial = x_ecliptic;
    const y_equatorial = cosEpsilon * y_ecliptic - sinEpsilon * z_ecliptic;
    const z_equatorial = sinEpsilon * y_ecliptic + cosEpsilon * z_ecliptic;

    const ra = Math.atan2(y_equatorial, x_equatorial) * RAD_TO_DEG;
    const dec = Math.atan2(z_equatorial,
                           Math.sqrt(x_equatorial * x_equatorial + y_equatorial * y_equatorial)) * RAD_TO_DEG;
    const raNormalized = ra < 0 ? ra + 360 : ra;

    return {
      ra: raNormalized,
      dec: dec
    };
  }
}

/**
 * Format Julian Date to readable string
 */
export function formatJD(jd) {
  const date = jdToDate(jd);
  return date.toISOString().split('T')[0];
}